/**
 * Погодинна таблиця: години 01–24 у стовпцях, показники — рядками.
 *
 * Рядки виводяться завжди, навіть коли даних немає, щоб відсутність читалась як
 * відсутність, а не як порожній екран.
 *
 * Редагуються ДВА рядки, але поодинці:
 *   «Прогноз»            — ручні коригування моделі (окрема таблиця, модель не чіпається);
 *   «Завантажено на MMS» — внесок цієї СЕС у графік відпуску. Поки для доби ще немає
 *                          збереженого графіка, рядок підставляє значення прогнозу як
 *                          відправну точку: диспетчер бачить, від чого відштовхується,
 *                          і правит лише те, що дійсно відрізняється.
 *
 * Редагування MMS доступне лише поки не минув дедлайн відповідної версії — сервер
 * перевіряє це повторно й відмовляє з 409, навіть якщо кнопка лишилась активною.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import type { HourRow } from './ForecastMmsChart';
import { useFcmmsTheme } from '../theme';
import { FileSpreadsheet, Info, Lock, Pencil, Save, Undo2, Sun, Zap, Send, BatteryCharging, Coins, Clock, type LucideIcon } from 'lucide-react';

// Кольори показників — лише токени (§1). HTML/CSS читає var(--…) напряму,
// тож getComputedStyle-хук потрібен тільки для SVG-серій графіка.
const C_FORECAST = 'var(--series-3)';
const C_FACT = 'var(--ok)';
const C_MMS = 'var(--series-2)';
const C_PERF = 'var(--warn)';
const C_BESS = 'var(--warn)';   // бурштиновий — заряд/розряд УЗЕ (контраст із зеленим Факт)

/** Редагується лише графік до MMS; прогноз — довідковий рядок. */
type Mode = null | 'mms' | 'fact' | 'factsplit';

interface Props {
  hours: HourRow[];
  assetId: string;
  date: string;
  loading: boolean;
  canEdit: boolean;              // роль дозволяє правити
  canEditMms: boolean;           // версія MMS ще відкрита
  forceUnlock?: boolean;         // Лучки: правити минулі періоди (обхід дедлайну)
  mmsRevision: number | null;    // яку версію графіка правимо/дивимось
  mmsStatus?: string | null;
  onSaved: () => void;
  /** Година під курсором графіка. Своєї копії стану таблиця не тримає. */
  activeHour?: number | null;
}

const BRAND = 'var(--series-3)';

const n1 = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nInt = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { maximumFractionDigits: 0 });
// Заряд/Розряд УЗЕ одним рядком: +розряд у мережу / −заряд (одне зі значень на годину).
const bessCell = (h: HourRow): string => {
  const c = h.bessChargeKwh ?? 0, d = h.bessDischargeKwh ?? 0;
  if (!c && !d) return '—';
  const net = d - c;
  return (net > 0 ? '+' : net < 0 ? '−' : '') + n1(Math.abs(net));
};
// Розбір введеного числа: прибираємо роздільники тисяч (пробіл, NBSP, вузький
// NBSP) і зводимо КОЖНУ кому до крапки. Раніше "4 476,0" давало NaN, який
// мовчки писався як 0 — саме так обнулились години 12 і 14 за 21.09.
const parseKwh = (raw: string | undefined): number | null => {
  const t = (raw ?? '').replace(/\s/g, '').replace(/,/g, '.').trim();  // \s покриває NBSP/вузький NBSP
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};
const dmy = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');

export default function ForecastMmsTable({
  hours, assetId, date, loading, canEdit, canEditMms, mmsRevision, mmsStatus, onSaved, forceUnlock,
  activeHour = null,
}: Props) {
  const { t } = useTranslation();
  const T = useFcmmsTheme();
  // Лучки: якщо ревізію не обрано, редагуємо версію 1 (створюється при збереженні).
  const effRevision = mmsRevision ?? (forceUnlock ? 1 : null);
  // Ручний факт — лише для Лучок (forceUnlock) і з 01.09.2026 (затримка збору телеметрії).
  const factEditable = !!forceUnlock && date >= '2026-09-01';
  // ── Підсвітка години під курсором графіка ──────────────────────────────
  // Геометрію беремо з САМОЇ клітинки заголовка, а не рахуємо по ширині
  // таблиці: підсвітка лишиться правильною, навіть якщо colgroup колись
  // зміниться, і не залежить від масштабу чи ширини вікна.
  // offsetLeft — величина з розкладки, тож горизонтальний скрол її не зсуває,
  // а накладка лежить усередині того самого контейнера й скролиться разом.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const [marker, setMarker] = useState<{ left: number; width: number } | null>(null);

  const measureMarker = useCallback(() => {
    const row = headRowRef.current;
    if (!row || activeHour == null) { setMarker(null); return; }
    const i = hours.findIndex((h) => h.hour === activeHour);
    // +1 — перша колонка службова (іконки рядків), годинною вона не рахується
    const cell = i < 0 ? null : (row.children[i + 1] as HTMLElement | undefined);
    if (!cell) { setMarker(null); return; }
    setMarker({ left: cell.offsetLeft, width: cell.offsetWidth });
  }, [activeHour, hours]);

  useLayoutEffect(() => { measureMarker(); }, [measureMarker]);

  // Ресайз вікна, зміна масштабу, поява/зникнення скролбару — перезаміряємо.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measureMarker());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureMarker]);

  const TABAKI_ID = '12a78662-a28d-40d5-9eb7-e83ebb158b41';
  const todayKyiv = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Kyiv' });
  const factSplitEditable = assetId === TABAKI_ID && date < todayKyiv;
  const [mode, setMode] = useState<Mode>(null);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [draftPv, setDraftPv] = useState<Record<number, string>>({});
  const [draftBess, setDraftBess] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [loadedAt, setLoadedAt] = useState<string>('');

  // Позначка часу оновлення — коли до таблиці прийшли свіжі дані.
  useEffect(() => {
    if (loading) return;
    setLoadedAt(new Date().toLocaleTimeString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { hour: '2-digit', minute: '2-digit' }));
  }, [hours, loading]);

  useEffect(() => { setMode(null); setDraft({}); setDraftPv({}); setDraftBess({}); setMsg(null); }, [assetId, date, mmsRevision]);

  /** Поточне значення рядка, який редагується. */
  const mmsSeeded = hours.some((h) => h.mmsIsSeeded);
  /** Значення в редагованому рядку: збережений графік, інакше — підставлений прогноз. */
  const seedOf = (h: HourRow) => mode === 'fact' ? h.factKwh : (h.mmsKwh ?? h.forecastKwh);

  const startEdit = (m: Exclude<Mode, null> = 'mms') => {
    const d: Record<number, string> = {};
    const seedFor = (h: HourRow) => m === 'fact' ? h.factKwh : (h.mmsKwh ?? h.forecastKwh);
    hours.forEach((h) => { const v = seedFor(h); d[h.hour] = v == null ? '' : String(v); });
    setDraft(d); setMode(m); setMsg(null);
  };
  const cancelEdit = () => { setDraft({}); setDraftPv({}); setDraftBess({}); setMode(null); setMsg(null); };
  const startFactSplit = async () => {
    const dp: Record<number, string> = {}; const db: Record<number, string> = {};
    try {
      const r = await authFetch(`${API_BASE_URL}/dispatch/forecast-mms/manual-fact?assetId=${assetId}&date=${date}`);
      const j = await r.json().catch(() => ({} as any));
      const map = new Map<number, any>((j.rows || []).map((x: any) => [Number(x.hour), x]));
      hours.forEach((h) => {
        const ex: any = map.get(h.hour);
        if (ex && (Number(ex.pv_kwh) || Number(ex.bess_kwh))) {
          dp[h.hour] = Number(ex.pv_kwh) ? String(Number(ex.pv_kwh)) : '';
          db[h.hour] = Number(ex.bess_kwh) ? String(Number(ex.bess_kwh)) : '';
        } else { dp[h.hour] = h.factKwh == null ? '' : String(h.factKwh); db[h.hour] = ''; }
      });
    } catch { hours.forEach((h) => { dp[h.hour] = h.factKwh == null ? '' : String(h.factKwh); db[h.hour] = ''; }); }
    setDraftPv(dp); setDraftBess(db); setMode('factsplit'); setMsg(null);
  };

  // Для прогнозу надсилаємо лише змінені години; для MMS — усі 24, бо графік
  // відпуску подається цілим масивом і «часткового» графіка не буває.
  // Графік відпуску подається цілим масивом — «часткового» графіка не буває,
  // тому надсилаємо всі 24 години, а не лише змінені.
  const payloadHours = useMemo(() => {
    if (!mode) return [];
    return hours.map((h) => {
      const v = parseKwh(draft[h.hour]);
      return { hour: h.hour, kwh: v == null ? 0 : v };
    });
  }, [mode, draft, hours]);

  const dirtyCount = useMemo(() => {
    if (mode === 'factsplit') return hours.length;
    if (!mode) return 0;
    return hours.filter((h) => {
      const v = parseKwh(draft[h.hour]);
      const before = seedOf(h);
      if (v == null) return before != null;
      return before == null || Math.abs(v - before) >= 0.05;
    }).length;
  }, [mode, draft, draftPv, draftBess, hours]);

  const save = async () => {
    if (!dirtyCount) { setMsg({ kind: 'err', text: t('ak_dsp2_no_changes_to_save') }); return; }
    setSaving(true); setMsg(null);
    try {
      if (mode === 'factsplit') {
        const payload = hours.map((h) => ({
          hour: h.hour,
          pvKwh: parseKwh(draftPv[h.hour]) ?? 0,
          bessKwh: parseKwh(draftBess[h.hour]) ?? 0,
        }));
        const rf2 = await authFetch(`${API_BASE_URL}/dispatch/forecast-mms/manual-fact`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assetId, date, rows: payload }),
        });
        const bf2 = await rf2.json().catch(() => ({}));
        if (!rf2.ok) throw new Error(bf2?.message || bf2?.error || `HTTP ${rf2.status}`);
        setMode(null); setDraftPv({}); setDraftBess({});
        setMsg({ kind: 'ok', text: t('ak_dsp2_actual_pv_bess_discharge_saved_h_for', { n: bf2.upserted ?? 0, d: dmy(date) }) });
        onSaved();
        return;
      }
      if (mode === 'fact') {
        // WYSIWYG (відкат 22.09): зберігаємо рівно те, що ввів диспетчер — це
        // НЕТ на приєднанні, та сама величина, що йде в дайджест. Конверсію
        // PV↔НЕТ прибрано: вона обрізала значення в 0, коли введене менше за
        // заряд УЗЕ (на екран виходив сам заряд), і подвоювала розряд.
        const byHour = new Map(hours.map((h) => [h.hour, h]));
        // Надсилаємо ЛИШЕ змінені години. Раніше писались усі 24: недоторкані
        // «заморожувались» ручним шаром і переставали оновлюватись із
        // телеметрії — сусідні години виглядали як довільно змінені.
        const netHours = payloadHours
          .filter((p) => {
            const before = byHour.get(p.hour)?.factKwh ?? null;
            return before == null || Math.abs(p.kwh - before) >= 0.05;
          })
          .map((p) => ({ hour: p.hour, kwh: Math.max(0, Math.round(p.kwh * 10) / 10) }));
        if (!netHours.length) {
          setSaving(false);
          setMsg({ kind: 'err', text: t('ak_dsp2_no_changes_to_save') });
          return;
        }
        const rf = await authFetch(`${API_BASE_URL}/comdisp/manual-fact`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assetId, date, hours: netHours }),
        });
        const bf = await rf.json().catch(() => ({}));
        if (!rf.ok) throw new Error(bf?.message || bf?.error || `HTTP ${rf.status}`);
        setMode(null); setDraft({});
        setMsg({ kind: 'ok', text: t('ak_dsp2_actual_generation_saved_h_for', { n: bf.upserted ?? 0, d: dmy(date) }) });
        onSaved();
        return;
      }
      const r = await authFetch(`${API_BASE_URL}/dispatch/forecast-mms/mms-plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date, revision: effRevision, hours: payloadHours, ...(forceUnlock ? { force: true } : {}) }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.message || body?.error || `HTTP ${r.status}`);
      setMode(null); setDraft({});
      setMsg({
        kind: 'ok',
        text: t('ak_dsp2_mms_schedule_saved_version_entry_added', { rev: body.revision, own: n1(body.assetTotalKwh), grp: n1((body.groupTotalMwh ?? 0) * 1000) }),
      });
      onSaved();
    } catch (e: any) {
      setMsg({ kind: 'err', text: e?.message || t('ak_dsp2_failed_to_save') });
    } finally { setSaving(false); }
  };

  const downloadXlsx = async () => {
    setXlsxBusy(true);
    try {
      const r = await authFetch(`${API_BASE_URL}/dispatch/forecast-mms/day.xlsx?assetId=${assetId}&date=${date}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `helios_forecast_mms_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e: any) {
      setMsg({ kind: 'err', text: `XLSX: ${e?.message || t('ak_dsp2_error')}` });
    } finally { setXlsxBusy(false); }
  };

  const btn = (label: string, onClick: () => void,
               opts: { disabled?: boolean; primary?: boolean; Icon?: LucideIcon; title?: string } = {}) => (
    <button onClick={onClick} disabled={opts.disabled} title={opts.title}
      className="text-xs rounded-lg px-3 py-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
      style={{
        background: opts.primary && !opts.disabled ? BRAND : T.panelBg,
        color: opts.disabled ? T.textDim : (opts.primary ? '#fff' : T.textMain),
        border: `1px solid ${opts.primary && !opts.disabled ? BRAND : T.cardBorder}`,
        cursor: opts.disabled ? 'not-allowed' : 'pointer', opacity: opts.disabled ? 0.55 : 1,
      }}>
      {opts.Icon && <opts.Icon size={14} strokeWidth={2} />}
      {label}
    </button>
  );

  const cell: any = {
    padding: '7px 4px', fontSize: 11.5, textAlign: 'center', color: T.tableCellText,
    borderBottom: `1px solid ${T.cardBorder}`, borderRight: `1px solid ${T.cardBorder}`,
    whiteSpace: 'nowrap', overflow: 'hidden',
  };
  const head: any = { ...cell, fontWeight: 700, color: T.tableHeadText, background: T.tableHeadBg };
  // Роздільник задано тінню, а не рамкою: при border-collapse: collapse рамка
  // належить сітці таблиці, а не комірці, тож «липкий» стовпчик не забирав її з
  // собою — під час прокручування годин лінія зникала. Тінь малюється разом з
  // елементом і лишається на місці. Ширина не фіксована: whiteSpace: nowrap
  // підганяє стовпчик під найдовший підпис і не більше.
  const label: any = {
    ...cell, textAlign: 'left', fontWeight: 600, color: T.tableHeadText, background: T.cardBg,
    position: 'sticky', left: 0, zIndex: 2,
    borderRight: 'none',
    boxShadow: `inset -1px 0 0 ${T.sep}`,
    paddingLeft: 10, paddingRight: 12,
  };

  // Підписи рядків замінено іконками (повна назва — у title при наведенні).
  const ROWS: { key: string; name: string; Icon: LucideIcon; color?: string; get: (h: HourRow) => string }[] = [
    { key: 'forecast', name: t('ak_dsp2_generation_forecast_kwh'), Icon: Sun, color: C_FORECAST, get: (h) => n1(h.forecastKwh) },
    { key: 'fact', name: t('ak_dsp2_actual_generation_kwh'), Icon: Zap, color: C_FACT, get: (h) => n1(h.factKwh) },
    { key: 'mms', name: t('ak_dsp2_uploaded_to_mms_kwh'), Icon: Send, color: C_MMS, get: (h) => n1(h.mmsKwh) },
    { key: 'bess', name: t('ak_dsp2_bess_charge_discharge_kwh'), Icon: BatteryCharging, color: C_BESS, get: (h) => bessCell(h) },
    { key: 'rdn', name: t('ak_dsp2_dam_price_uah_mwh'), Icon: Coins, get: (h) => (h.rdnPriceUahMwh == null ? '—' : nInt(h.rdnPriceUahMwh)) },
  ];

  return (
    <div>
      {/* Заголовок винесено ЗА плитку — секційний хедер над карткою. */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2.5 px-1">
        <div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-base font-bold" style={{ color: T.textMain }}>{t('ak_dsp2_hourly_data')}</span>
            <span className="text-[11px]" style={{ color: T.textMuted }}>
              {dmy(date)}{loadedAt ? ` · ${loadedAt}` : ''}
              {mmsRevision ? ` · ${t('ak_dsp2_mms_schedule_ver', { r: mmsRevision })}${mmsStatus ? ` (${mmsStatus})` : ''}` : ''}
            </span>
          </div>
          {mode && (
            <div className="text-[11px] mt-0.5" style={{ color: C_PERF }}>
              {mode === 'factsplit' ? t('ak_dsp2_editing_actuals_pv_generation_bess_dis') : mode === 'fact' ? t('ak_dsp2_editing_the_actual_row') : t('ak_dsp2_editing_the_uploaded_to_mms_row_versio', { rev: effRevision })} · {t('ak_dsp2_hours_changed', { n: dirtyCount })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {btn(xlsxBusy ? t('ak_dsp2_preparing') : 'XLSX', downloadXlsx, { disabled: xlsxBusy || loading, Icon: FileSpreadsheet })}
          {canEdit && !mode && factEditable && btn('', () => startEdit('fact'), {
            Icon: Zap,
            title: t('ak_dsp2_edit_actuals_enter_actual_generation_m'),
          })}
          {canEdit && !mode && factSplitEditable && btn('', () => startFactSplit(), {
            Icon: Zap,
            title: t('ak_dsp2_edit_actuals_enter_manually_pv_generat'),
          })}
          {canEdit && !mode && btn('', () => startEdit('mms'), {
            disabled: loading || !canEditMms || (!mmsRevision && !forceUnlock),
            Icon: Sun,
            title: (!mmsRevision && !forceUnlock) ? t('ak_dsp2_no_active_schedule_version')
              : !canEditMms ? t('ak_dsp2_version_deadline_has_passed_the_schedu')
              : t('ak_dsp2_edit_forecast_edit_the_uploaded_to_mms'),
          })}
          {mode && btn(t('ak_dsp2_cancel'), cancelEdit, { disabled: saving, Icon: Undo2 })}
          {mode && btn(saving ? t('ak_dsp2_saving') : t('ak_dsp2_save'), save,
            { disabled: saving || !dirtyCount, primary: true, Icon: Save })}
        </div>
      </div>

      <div className="rounded-xl p-4 sm:p-5" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}>
      {msg && (
        <div className="text-xs mb-3 rounded-lg px-3 py-2"
          style={{
            background: msg.kind === 'ok' ? 'rgba(46,157,83,0.10)' : 'rgba(199,122,27,0.10)',
            border: `1px solid ${msg.kind === 'ok' ? 'rgba(46,157,83,0.35)' : 'rgba(199,122,27,0.35)'}`,
            color: msg.kind === 'ok' ? T.bannerOkText : T.bannerErrText,
          }}>{msg.text}</div>
      )}

      {/* Поки годин немає (рефреш/завантаження) — плейсхолдер замість fixed-таблиці:
          з порожнім hours колонки розтягуються й «стрибають» при появі даних. */}
      {!hours.length ? (
        <div className="rounded-lg py-10 text-center text-sm"
          style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 8, color: T.textMuted }}>
          {loading ? t('ak_dsp2_loading') : t('ak_dsp2_no_data_for_this_day')}
        </div>
      ) : (
      <div ref={scrollRef} className="overflow-x-auto"
        style={{ position: 'relative', border: `1px solid ${T.cardBorder}`, borderRadius: 8 }}>
        {/* Активна година: підсвітка колонки (без вертикалі — лишається тільки
            заливка). Підсвітка саме тут, а не в графіку — у графіку стовпчики
            самі показують годину, а в таблиці всі колонки однакові, тож без
            заливки око губить потрібну.
            Накладка лежить ПІД таблицею (z-index 0 проти 1), тож цифри, іконки
            й поля вводу нічим не перекриті — читаються так само, як і поза
            підсвіткою. Контейнер зі скролом обрізає її своїми межами, тому
            в хедер, легенду чи проміжок між блоками вона вийти не може. */}
        {marker && (
          <div aria-hidden style={{
            position: 'absolute', top: 0, bottom: 0,
            left: marker.left, width: marker.width,
            background: 'color-mix(in srgb, var(--text) 7%, transparent)',
            pointerEvents: 'none', zIndex: 0,
          }} />
        )}
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', position: 'relative', zIndex: 1 }}>
          {/* Ширини під вісь графіка над таблицею: ліва колонка = YAxis(76)+margin.left(8)=84px,
              права порожня = margin.right(14) — так години таблиці стають під годинами графіка. */}
          <colgroup>
            <col style={{ width: 84 }} />
            {hours.map((h) => <col key={h.hour} />)}
            <col style={{ width: 14 }} />
          </colgroup>
          <tbody>
            <tr ref={headRowRef}>
              <td style={label} title={t('ak_dsp2_hour')}>
                <Clock size={16} strokeWidth={2} style={{ color: T.textMuted, display: 'block', margin: '0 auto' }} />
              </td>
              {hours.map((h) => <td key={h.hour} style={head}>{String(h.hour).padStart(2, '0')}</td>)}
              <td style={head} />
            </tr>

            {(mode === 'factsplit'
              ? ROWS.flatMap((r) => r.key === 'fact'
                  ? [{ key: 'factpv', name: t('ak_dsp2_actual_pv_generation_kwh'), Icon: Zap, color: C_FACT, get: (h: HourRow) => n1(h.factKwh) },
                     { key: 'factbess', name: t('ak_dsp2_actual_bess_discharge_kwh'), Icon: BatteryCharging, color: C_MMS, get: (_h: HourRow) => '' }]
                  : [r])
              : ROWS).map((row, ri) => {
              const editable = (mode === 'mms' && row.key === 'mms') || (mode === 'fact' && row.key === 'fact') || (mode === 'factsplit' && (row.key === 'factpv' || row.key === 'factbess'));
              const RowIcon = (row as any).Icon as LucideIcon | undefined;
              return (
                <tr key={`${row.name}-${ri}`}>
                  <td style={label} title={row.name}>
                    {RowIcon
                      ? <RowIcon size={16} strokeWidth={2} style={{ color: row.color || T.textMuted, display: 'block', margin: '0 auto' }} />
                      : (<>{row.color && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: row.color, marginRight: 7 }} />}{row.name}</>)}
                  </td>
                  {hours.map((h) => {
                    if (editable) {
                      const dmap = row.key === 'factpv' ? draftPv : row.key === 'factbess' ? draftBess : draft;
                      const dset = row.key === 'factpv' ? setDraftPv : row.key === 'factbess' ? setDraftBess : setDraft;
                      const seed = seedOf(h);
                      const dirty = mode === 'factsplit' ? (dmap[h.hour] ?? '') !== '' : (dmap[h.hour] ?? '') !== (seed == null ? '' : String(seed));
                      return (
                        <td key={h.hour} style={{ ...cell, padding: 3 }}>
                          <input
                            value={dmap[h.hour] ?? ''}
                            onChange={(e) => { const v = e.target.value; dset((d) => ({ ...d, [h.hour]: v })); }}
                            inputMode="decimal"
                            style={{
                              width: '100%', textAlign: 'center', fontSize: 11, padding: '5px 2px',
                              background: T.innerBg, color: T.textMain, borderRadius: 6,
                              border: `1px solid ${dirty ? C_PERF : T.cardBorder}`, outline: 'none',
                            }} />
                        </td>
                      );
                    }
                    const overridden = row.key === 'forecast' && h.isOverridden;
                    // Значення підставлене з прогнозу: показуємо приглушено й курсивом,
                    // щоб його не сплутали з реально збереженим графіком.
                    const seeded = row.key === 'mms' && !!h.mmsIsSeeded && h.mmsKwh != null;
                    return (
                      <td key={h.hour}
                        title={overridden
                          ? t('ak_dsp2_manually_adjusted_model_kwh', { by: h.editedBy ? ` (${h.editedBy})` : '', m: n1(h.modelKwh) })
                          : seeded ? t('ak_dsp2_value_taken_from_the_forecast_the_sche') : undefined}
                        style={{
                          ...cell, color: row.color || T.textMain,
                          fontWeight: row.color ? 600 : 400,
                          background: overridden ? 'color-mix(in srgb, var(--warn) 12%, transparent)' : undefined,
                        }}>
                        {row.get(h)}
                        {overridden && <Pencil size={9} strokeWidth={2.4} style={{ marginLeft: 4, color: C_PERF, display: 'inline' }} />}
                      </td>
                    );
                  })}
                  <td style={cell} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {!canEdit && (
        <div className="text-[11px] mt-3" style={{ color: T.textDim }}>
          {t('ak_dsp2_editing_is_available_to_the_operator_a')}
        </div>
      )}
      {canEdit && !mode && !canEditMms && (
        <div className="text-[11px] mt-3 flex items-start gap-1.5" style={{ color: T.textDim }}>
          <Lock size={13} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            {mmsRevision
              ? t('ak_dsp2_the_schedule_for_this_day_can_no_longe', { rev: mmsRevision })
              : t('ak_dsp2_the_schedule_for_this_day_cannot_be_ed')}
            {t('ak_dsp2_a_schedule_can_only_be_submitted_for_a')}{' '}<b>D+1</b>{t('ak_dsp2_or_a_later_date')}
          </span>
        </div>
      )}
      {canEdit && mode === 'mms' && (
        <div className="text-[11px] mt-3 flex items-start gap-1.5" style={{ color: T.textDim }}>
          <Info size={13} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            {t('ak_dsp2_forecast_values_are_pre_filled_edit_on')}
          </span>
        </div>
      )}
      </div>
    </div>
  );
}
